/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {
    Box,
    Container,
    Flex,
    Spinner,
    useBreakpointValue,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import Seo from '../../../components/seo'
import {useOptimizelyPage} from '../../../hooks/use-optimizely-page'
import {renderComponentsSwitch} from '../../../components/optimizely/utils/contentRenderer'
import {GDIDynamicContentProvider} from '../../../components/optimizely/gdi/utils/gdi-dynamic-content-context'
import {useBrand} from '../../../contexts/brand-context'

const EmptyCart = ({isMiniCart}) => {
    const styles = useMultiStyleConfig('Cart')
    const {formatMessage} = useIntl()
    const {isGarage} = useBrand()
    const {data: optimizelyData, isLoading} = useOptimizelyPage('cart-minicart')

    const cartPageTitle = isGarage
        ? formatMessage({
              id: 'cart.page.title.garage',
              defaultMessage: 'My Shopping Cart | Garage'
          })
        : formatMessage({
              id: 'cart.page.title.dynamite',
              defaultMessage: 'My Shopping Cart | Dynamite'
          })
    const cartPageDescription = isGarage
        ? formatMessage({
              id: 'cart.page.description.garage',
              defaultMessage: "Find everything you're looking for? Review your cart here before checking out."
          })
        : formatMessage({
              id: 'cart.page.description.dynamite',
              defaultMessage: "Find everything you're looking for? Review your cart here before checking out."
          })

    /* Disable carousel viewport bleed only on desktop (lg+) so the authored
     * layout stays within the constrained container; keep the edge-to-edge
     * bleed on mobile so the carousel isn't inset by the container padding. */
    const disableViewportBleed = useBreakpointValue({base: false, lg: true})

    const pageData = optimizelyData?.item || optimizelyData?.page || optimizelyData
    const contentArea = isMiniCart
        ? pageData?.EmptyMinicartContent
        : pageData?.EmptyCartContent

    return (
        <Box data-testid="sf-cart-empty" {...styles.emptyCartRoot}>
            <Seo title={cartPageTitle} description={cartPageDescription} noIndex noFollow />
            <Container
                {...styles.emptyCartContainer}
                px={isMiniCart ? 0 : [4, 6, 6, 4]}
                paddingTop={{base: 8, lg: isMiniCart ? 0 : 8}}
            >
                {isLoading ? (
                    <Flex justify="center" align="center" py={8}>
                        <Spinner />
                    </Flex>
                ) : (
                    <GDIDynamicContentProvider
                        value={{
                            disableViewportBleed: !!disableViewportBleed,
                            carouselPeekInset: !!disableViewportBleed
                        }}
                    >
                        {contentArea?.map((item) => {
                            const block = item?.ContentLink?.Expanded
                            if (!block) return null
                            return (
                                <Box key={item.ContentLink.GuidValue}>
                                    {renderComponentsSwitch(block)}
                                </Box>
                            )
                        })}
                    </GDIDynamicContentProvider>
                )}
            </Container>
        </Box>
    )
}

EmptyCart.propTypes = {
    isMiniCart: PropTypes.bool
}

export default EmptyCart
